import { Fragment, useCallback, useState, useEffect, useRef } from 'react';
import { toJS } from 'mobx';
import cc from 'classnames';
import { observer } from 'decorators';
import { makeStyles, useAction } from 'hooks';
import { Typography, CardActions, Tooltip, FormHelperText } from '@material-ui/core';
import Grid from 'components/grid';
import Button from 'components/button';
import { CardListItem, CardListItemContent, CardDetails } from 'components/lists';
import { ItemFab } from 'components/titleSection';
import userService from 'services/data/user';

import { insurerTitle, insurerSubTitle, insurerDetails } from 'services/forms/insurer';

import InsurerInput, { InsurancePhotoInput } from 'pages/billing/bill/insurerInput';
import InsurerCardPhotos from 'pages/billing/bill/insuranceCardPhoto';

import { Plus, Pencil, Delete, SwapHorizontal } from 'mdi-material-ui';

const noop = () => {};
export default observer(function InsuranceSharedView({ store, form, inline, disabled, showAddOnDisabled, onEditingChanged, onUpdated, headingClass, onRefresh: handleRefresh, editingState, onEditingStateChanged, isTentative, clientId, docId, hideCopay }) {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(handleRefresh || noop);

  const { insurers, copay } = store;
  const lastAddValue = useRef(null);

  // May have internal state or controlled state
  const [ editingStateInternal, setEditingStateInternal ] = useState(editingState == null ? -1 : editingState);
  editingState = editingState == null ? editingStateInternal : editingState;

  const setEditingState = useCallback(s => {
    onEditingStateChanged && onEditingStateChanged(s);
    setEditingStateInternal(s);
  }, [ onEditingStateChanged ]);

  // Need a clearinghouse saved otherwise an error will happen
  const defaultClearinghouse = userService.clearinghouse.value?.clearing.find(c => c.enabled)?.clearing || 'kalix';
  const handleAdd = useAction(e => {
    try { e.preventDefault(); } catch {}

    lastAddValue.current = toJS(store.insurers);

    const n = form.$('insurers').add();
    const ins = n.value;
    ins.clearing = defaultClearinghouse;
    store.insurers.push(n.value);
    setEditingState(store.insurers.length - 1);
    onEditingChanged && onEditingChanged(true);
    onUpdated && onUpdated();
  }, [ form, onEditingChanged, setEditingState, store, defaultClearinghouse ]);

  const handleDelete = useAction(index => {
    if (index === editingState) {
      setEditingState(-1);
      onEditingChanged && onEditingChanged(false);
    }

    store.insurers.splice(index, 1);
    if (index === 0 && !hideCopay) { store.copay = null; }

    const update = { insurers: toJS(store.insurers) };
    if (!hideCopay) { update.copay = store.copay; }
    form.update(update);
    onUpdated && onUpdated();
  }, [ store, setEditingState ]);

  const handleEdit = useAction(index => {
    if (lastAddValue.current) {
      const update = { insurers: lastAddValue.current };
      if (!hideCopay) { update.copay = store.copay; }
      form.update(update);
      lastAddValue.current = null;
    }
    setEditingState(index);
    onEditingChanged && onEditingChanged(true);
  }, [ form, store, onEditingChanged, setEditingState, hideCopay ]);

  const handleEndEdit = useAction(save => {
    setEditingState(-1);
    onEditingChanged && onEditingChanged(false);
    if (save) {
      const { insurers, copay } = form.values();
      insurers.forEach(i => { if (!i.clearing) { i.clearing = defaultClearinghouse; } });
      store.insurers = insurers;
      if (!hideCopay) { store.copay = copay; }
      form.$('insurers').validate()
        .then(() => {
          onUpdated && onUpdated();
          return null;
        })
        .catch(() => {})
        .done();
    } else {
      const update = { insurers: lastAddValue.current || toJS(store.insurers) };
      if (!hideCopay) { update.copay = store.copay; }
      form.update(update);
    }
    lastAddValue.current = null;
  }, [ form, store, setEditingState, defaultClearinghouse, hideCopay ]);

  const handleSwitch = useAction(() => {
    const cur = store.insurers;
    store.insurers = [ cur[1], cur[0] ];
    form.update({
      insurers: toJS(store.insurers)
    });
    handleEndEdit(true);
  }, [ store, handleEndEdit ]);

  if (!insurers) { return null; }

  const error = form.$('insurers').error;
  const details = insurers.map((i, index) => ({
    title: insurerTitle(index),
    subTitle: insurerSubTitle(i),
    details: insurerDetails(i, (index === 0 && !hideCopay) ? copay : null, { simplified: true }),
    data: i
  }));

  const canSwitch = details.length === 2;
  const addText = `Add ${(details.length === 0 ? 'Primary' : 'Secondary')} Insurance`;
  return <Fragment>
    <Typography variant="h6" className={headingClass}>Insurance Details</Typography>
    <Typography paragraph>Please enter your insurance details below.</Typography>
    <Grid container alignItems="stretch" spacing={2}>
      { details.map((i, index) => (
        editingState === index
          ? <EditInsurerView key={index} inline={inline} error={error} form={form} editingInsurer={form.$(`insurers.${index}`)} onEditEnd={handleEndEdit} onRefresh={handleRefresh} isTentative={isTentative} clientId={clientId} docId={docId} hideCopay={hideCopay} />
          : <InsurerItem key={index} insurer={i} index={index} onDelete={handleDelete} onEdit={handleEdit} onSwitch={handleSwitch} disabled={disabled || editingState !== -1} isTentative={isTentative} clientId={clientId} docId={docId} canSwitch={canSwitch} />
      ))}
      { (!disabled || showAddOnDisabled) && editingState === -1 && details.length < 2 &&
        <ItemFab onClick={handleAdd} disabled={disabled} aria-label={addText}>
          <Plus />
          <Typography align="center" variant="h6">
            { addText }
          </Typography>
        </ItemFab>
      }
    </Grid>
    <FormHelperText error={!!error}>{error}</FormHelperText>
  </Fragment>;
});

const InsurerItem = observer(function InsurerItem({ insurer, index, disabled, onDelete, onEdit, onSwitch: handleSwitch, isTentative, clientId, docId, canSwitch }) {
  const handleDelete = useCallback(() => onDelete(index), [ onDelete, index ]);
  const handleEdit = useCallback(() => onEdit(index), [ onEdit, index ]);

  return <Grid item xs={12} sm={6}>
    <CardListItem>
      <CardListItemContent>
        <Grid container direction="column" spacing={2}>
          <Grid item>
            <Typography component="div" variant="h6">{ insurer.title }</Typography>
            <Typography component="div" color="textSecondary">{ insurer.subTitle }</Typography>
          </Grid>
          <Grid item>
            <InsurerCardPhotos insurer={insurer.data} isTentative={isTentative} clientId={clientId} docId={docId} />
          </Grid>
          <Grid item>
            { insurer.details.map((de, i) => <CardDetails key={i} title={de.name} value={de.value} value2={de.value2} noTitle={de.noTitle} />) }
          </Grid>
          { insurer.additionalDetails &&
            <Grid item>
              <Typography component="pre" style={{ whiteSpace: 'pre-wrap' }}>{insurer.additionalDetails}</Typography>
            </Grid>
          }
        </Grid>
      </CardListItemContent>
      { !disabled &&
        <CardActions>
          <Tooltip title="Delete"><Button icon onClick={handleDelete}><Delete /></Button></Tooltip>
          { canSwitch && <Tooltip title="Swap"><Button onClick={handleSwitch} startIcon={<SwapHorizontal />}>Swap</Button></Tooltip> }
          <Grid item xs />
          <Tooltip title="Edit"><Button icon onClick={handleEdit}><Pencil /></Button></Tooltip>
        </CardActions>
      }
    </CardListItem>
  </Grid>;
});

const useEditInsurerStyles = makeStyles(theme => ({
  error: {
    border: `2px solid ${theme.palette.error.main}`
  },
  hide: {
    display: 'none'
  },
  cropContainer: {
    padding: theme.spacing(2)
  }
}));

const EditInsurerView = observer(function EditInsurerView({ inline, error, form, editingInsurer, onEditEnd, onRefresh: handleRefresh, isTentative, clientId, docId, hideCopay }) {
  const classes = useEditInsurerStyles();
  const inlinePortalRef = useRef();
  const [ isShowingEditor, setIsShowingEditor ] = useState(false);
  const [ isFrontEditor, setIsFrontEditor ] = useState(false);

  const handleCancel = useCallback(() => onEditEnd(false), [ onEditEnd ]);
  const handleSave = useCallback(() => onEditEnd(true), [ onEditEnd ]);
  const handleShowEditor = useCallback((e, key) => {
    setIsShowingEditor(e);
    setIsFrontEditor(key === 'front');
  }, []);

  return <Grid item xs={12} sm={6}>
    <CardListItem className={cc(error && classes.error)} style={{ position: 'relative' }}>
      <div className={cc(classes.cropContainer, !isShowingEditor && classes.hide)}>
        <Typography variant="h6">Crop Image ({ isFrontEditor ? 'Front of Card' : 'Back of Card' })</Typography>
        <div ref={inlinePortalRef} />
      </div>
      <CardListItemContent className={cc(isShowingEditor && classes.hide)}>
        <InsurerInput
          simplified
          inlineUpload={inline}
          isTentative={isTentative}
          inlinePortalRef={inlinePortalRef}
          onShowEditor={handleShowEditor}
          clientId={clientId}
          docId={docId}
          form={form}
          field={editingInsurer.path}
          copayPath={hideCopay ? undefined : 'copay' }
          onRefresh={handleRefresh}
        />
      </CardListItemContent>
      <CardActions className={cc(isShowingEditor && classes.hide)}>
        <Button onClick={handleCancel}>Cancel</Button>
        <Grid item xs />
        <Button color="primary" onClick={handleSave}>Save</Button>
      </CardActions>
    </CardListItem>
  </Grid>;
});

export const InsurancePhotoId = observer(function InsurancePhotoId({ form, inline, disabled, headingClass, onRefresh: handleRefresh, isTentative, clientId, docId }) {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(handleRefresh || noop);
  const classes = useEditInsurerStyles();

  const inlinePortalRef = useRef();
  const [ isShowingEditor, setIsShowingEditor ] = useState(false);

  const handleShowEditor = useCallback(e => setIsShowingEditor(e), []);

  const error = form.$('insurerPhotoId').error;
  const hasError = !isShowingEditor && !!error;
  const showingInlineEditor = inline && isShowingEditor;

  return <Fragment>
    <Typography variant="h6" className={headingClass}>Identity Verification</Typography>
    <Typography variant="subtitle1" paragraph>Please upload a copy of your government-issued photo ID, such as a driver&apos;s license or passport.</Typography>
    <Grid container alignItems="stretch" spacing={2}>
      <Grid item xs={12} sm={6} lg={4} className={cc(showingInlineEditor && classes.hide)}>
        <div className={cc(hasError && classes.error)}>
          <InsurancePhotoInput
            inline={inline}
            inlinePortalRef={inlinePortalRef}
            disabled={disabled}
            cardKey="photoId"
            form={form}
            isTentative={isTentative}
            clientId={clientId}
            docId={docId}
            onShowEditor={handleShowEditor}
            onRefresh={handleRefresh}
          />
        </div>
      </Grid>
      <Grid item xs={12} sm={6} className={cc(!showingInlineEditor && classes.hide)}>
        <CardListItem>
          <CardListItemContent>
            <div ref={inlinePortalRef} />
          </CardListItemContent>
        </CardListItem>
      </Grid>
    </Grid>
    <FormHelperText error={hasError}>{error}</FormHelperText>
  </Fragment>;
});